import { toRaw } from 'vue';

export type TSelectStrategyFn = (data: {
  id: unknown;
  value: boolean;
  selected: Map<unknown, 'on' | 'off' | 'indeterminate'>;
  children: Map<unknown, unknown[]>;
  parents: Map<unknown, unknown>;
  event?: Event;
}) => Map<unknown, 'on' | 'off' | 'indeterminate'>;

export type TSelectStrategyTransformInFn = (
  value: unknown[] | undefined,
  children: Map<unknown, unknown[]>,
  parents: Map<unknown, unknown>
) => Map<unknown, 'on' | 'off' | 'indeterminate'>;

export type TSelectStrategyTransformOutFn = (
  value: Map<unknown, 'on' | 'off' | 'indeterminate'>,
  children: Map<unknown, unknown[]>,
  parents: Map<unknown, unknown>
) => unknown[];

export type TSelectStrategy = {
  select: TSelectStrategyFn;
  in: TSelectStrategyTransformInFn;
  out: TSelectStrategyTransformOutFn;
};

export type TSelectStrategyProp =
  | 'single-leaf'
  | 'leaf'
  | 'independent'
  | 'single-independent'
  | 'classic'
  | TSelectStrategyFn;

export const independentSelectStrategy = (required?: boolean): TSelectStrategy => {
  const strategy: TSelectStrategy = {
    select: ({ id, value, selected }) => {
      id = toRaw(id);

      if (required && !value) {
        const on = Array.from(selected.entries()).reduce(
          (arr, [key, value]) => (value === 'on' ? [...arr, key] : arr),
          [] as unknown[]
        );
        if (on.length === 1 && on[0] === id) return selected;
      }

      selected.set(id, value ? 'on' : 'off');

      return selected;
    },
    in: (v, children, parents) => {
      let map = new Map();

      for (const id of v || []) {
        map = strategy.select({
          id,
          value: true,
          selected: new Map(map),
          children,
          parents
        });
      }

      return map;
    },
    out: (v) => {
      const arr = [];

      for (const [key, value] of v.entries()) {
        if (value === 'on') arr.push(key);
      }

      return arr;
    }
  };

  return strategy;
};

export const independentSingleSelectStrategy = (required?: boolean): TSelectStrategy => {
  const parentStrategy = independentSelectStrategy(required);

  return {
    select: ({ selected, id, ...rest }) => {
      id = toRaw(id);

      const singleSelected = selected.has(id) ? new Map([[id, selected.get(id)!]]) : new Map();
      return parentStrategy.select({ ...rest, id, selected: singleSelected });
    },
    in: (v, children, parents) => {
      let map = new Map();

      if (v?.length) {
        map = parentStrategy.in(v.slice(0, 1), children, parents);
      }

      return map;
    },
    out: (v, children, parents) => {
      return parentStrategy.out(v, children, parents);
    }
  };
};

export const leafSelectStrategy = (required?: boolean): TSelectStrategy => {
  const parentStrategy = independentSelectStrategy(required);

  return {
    select: ({ id, selected, children, ...rest }) => {
      id = toRaw(id);

      if (children.has(id)) return selected;

      return parentStrategy.select({ id, selected, children, ...rest });
    },
    in: parentStrategy.in,
    out: parentStrategy.out
  };
};

export const leafSingleSelectStrategy = (required?: boolean): TSelectStrategy => {
  const parentStrategy = independentSingleSelectStrategy(required);

  return {
    select: ({ id, selected, children, ...rest }) => {
      id = toRaw(id);

      if (children.has(id)) return selected;

      return parentStrategy.select({ id, selected, children, ...rest });
    },
    in: parentStrategy.in,
    out: parentStrategy.out
  };
};

export const classicSelectStrategy = (required?: boolean): TSelectStrategy => {
  const strategy: TSelectStrategy = {
    select: ({ id, value, selected, children, parents }) => {
      id = toRaw(id);

      const original = new Map(selected);
      const items = [id];

      while (items.length) {
        const item = items.shift()!;

        selected.set(item, value ? 'on' : 'off');

        if (children.has(item)) {
          items.push(...children.get(item)!);
        }
      }

      let parent = parents.get(id);

      while (parent) {
        const childrenIds = children.get(parent)!;
        const everySelected = childrenIds.every((cid) => selected.get(cid) === 'on');
        const noneSelected = childrenIds.every((cid) => !selected.has(cid) || selected.get(cid) === 'off');

        selected.set(parent, everySelected ? 'on' : noneSelected ? 'off' : 'indeterminate');

        parent = parents.get(parent);
      }

      if (required && !value) {
        const on = Array.from(selected.entries()).reduce(
          (arr, [key, value]) => (value === 'on' ? [...arr, key] : arr),
          [] as unknown[]
        );
        if (on.length === 0) return original;
      }

      return selected;
    },
    in: (v, children, parents) => {
      let map = new Map();

      for (const id of v || []) {
        map = strategy.select({
          id,
          value: true,
          selected: new Map(map),
          children,
          parents
        });
      }

      return map;
    },
    out: (v, children) => {
      const arr = [];

      for (const [key, value] of v.entries()) {
        if (value === 'on' && !children.has(key)) arr.push(key);
      }

      return arr;
    }
  };

  return strategy;
};
